import { assign, createActor, enqueueActions, setup } from "xstate";
import { fromStream } from "mobx-utils";
import { Step } from "../steps/v2/step";
import { Flow, FlowPosition, FlowState, Section } from "./types";
import { FlowFlexibility } from "@common/schemas";

export interface FlowStateMachineInput {
  flow: Flow;
  flowFlexibility: FlowFlexibility;
  initialFlowState: FlowState;
}

export interface FlowStateMachineContext extends FlowPosition {
  flow: Flow;
  flowFlexibility: FlowFlexibility;
  currentTime: number;
  eventQueue: FlowStateMachineEvent[];
}

const getFlowPosition = (context: FlowStateMachineContext): FlowPosition => ({
  currentSectionIndex: context.currentSectionIndex,
  currentStepIndex: context.currentStepIndex,
  flowKey: context.flow.key,
});

export interface FlowStateMachineHooks {
  onStepTransition?: (pos: FlowPosition, step: Step) => void;
  onWaitBlockingStep?: (pos: FlowPosition, step: Step) => void;
  onSectionTransition?: (pos: FlowPosition, section: Section) => void;
  onFlowFinish?: () => void;
  onStepRun?: (pos: FlowPosition, step: Step) => void;
  onStepDispose?: (pos: FlowPosition, step: Step) => void;
}

export interface CreateFlowStateActorParams {
  input: FlowStateMachineInput;
  hooks?: FlowStateMachineHooks;
}

type FlowStateMachineEvent =
  | { type: "unblock" }
  | { type: "mediaFinished" }
  | { type: "timeUpdate"; currentTime: number }
  | { type: "mediaPlaying" }
  | { type: "replay" }
  | { type: "forceProceedToNextStep" };

export const createFlowStateActor = ({ input, hooks }: CreateFlowStateActorParams) => {
  const flowStateMachine = setup({
    types: {
      input: {} as FlowStateMachineInput,
      context: {} as FlowStateMachineContext,
      events: {} as FlowStateMachineEvent,
    },
    guards: {
      isLastSection: ({ context }) => {
        const { currentSectionIndex, flow } = context;
        return currentSectionIndex === flow.sections.length - 1;
      },
      isStepRunTime: ({ context }) => {
        const { currentSectionIndex, currentStepIndex, flow, currentTime } = context;
        const section = flow.sections[currentSectionIndex];
        const step = section.steps[currentStepIndex];
        return currentTime >= step.start;
      },
      isStepEndTime: ({ context }) => {
        const { currentSectionIndex, currentStepIndex, flow, currentTime } = context;
        const section = flow.sections[currentSectionIndex];
        const step = section.steps[currentStepIndex];
        return currentTime >= step.end;
      },
      hasNextStep: ({ context }) => {
        const { currentSectionIndex, currentStepIndex, flow } = context;
        const section = flow.sections[currentSectionIndex];
        return currentStepIndex < section.steps.length - 1;
      },
      isLastStepInSection: ({ context }) => {
        const { currentSectionIndex, currentStepIndex, flow } = context;
        const section = flow.sections[currentSectionIndex];
        return currentStepIndex === section.steps.length - 1;
      },
      isBlockingStep: ({ context }) => {
        const { currentSectionIndex, currentStepIndex, flow } = context;
        const section = flow.sections[currentSectionIndex];
        const step = section.steps[currentStepIndex];
        return step.isBlocking;
      },
    },
    actions: {
      proceedNextSection: assign(({ context }) => {
        const { currentSectionIndex } = context;
        const nextSectionPosition = { sectionIndex: currentSectionIndex + 1, stepIndex: 0 };
        const section = context.flow.sections[nextSectionPosition.sectionIndex];
        hooks?.onSectionTransition?.(getFlowPosition(context), section);
        return {
          ...context,
          currentSectionIndex: nextSectionPosition.sectionIndex,
          currentStepIndex: nextSectionPosition.stepIndex,
          currentTime: 0,
        };
      }),
      proceedNextStep: assign(({ context }) => {
        const { currentSectionIndex, currentStepIndex, flow } = context;
        const section = flow.sections[currentSectionIndex];

        if (currentStepIndex < section.steps.length - 1) {
          return { ...context, currentStepIndex: currentStepIndex + 1 };
        }

        return context;
      }),
      setCurrentTime: assign(({ context, event }) => {
        if (event.type === "timeUpdate") {
          return { ...context, currentTime: event.currentTime };
        }
        return context;
      }),
      deferMediaFinishedEvent: assign({
        eventQueue: ({ context: { eventQueue }, event }) => [...eventQueue, event],
      }),
      drainDeferredMediaFinishedEvents: enqueueActions(({ context: { eventQueue }, enqueue }) => {
        eventQueue.forEach((event) => enqueue.raise(event));
        enqueue.assign({ eventQueue: [] });
      }),
      // hooks
      initStep: ({ context }) => {
        const step = context.flow.sections[context.currentSectionIndex].steps[context.currentStepIndex];
        hooks?.onStepTransition?.(getFlowPosition(context), step);
        step.init();
      },
      runStep: ({ context }) => {
        const step = context.flow.sections[context.currentSectionIndex].steps[context.currentStepIndex];
        step.run();
        hooks?.onStepRun?.(getFlowPosition(context), step);
      },
      finishStep: ({ context }) => {
        const step = context.flow.sections[context.currentSectionIndex].steps[context.currentStepIndex];
        step.finish();
      },
      disposeStep: ({ context }) => {
        const step = context.flow.sections[context.currentSectionIndex].steps[context.currentStepIndex];
        step.dispose();
        hooks?.onStepDispose?.(getFlowPosition(context), step);
      },
      finish: () => {
        hooks?.onFlowFinish?.();
      },
      waitBlockingStep: ({ context }) => {
        const step = context.flow.sections[context.currentSectionIndex].steps[context.currentStepIndex];
        hooks?.onWaitBlockingStep?.(getFlowPosition(context), step);
      },
    },
  }).createMachine({
    /** @xstate-layout N4IgpgJg5mDOIC5QDMA2B7A7gZQC4ENcwBiXASwFswBVABwkLAG0AGAXUVFvVjPPQB2nEAA9EARnEAWAJwA6ABxTxClgFZxagOzaAzFIA0IAJ4SFuxeekspu6bq0OAvk6NoseRnMz4+ASQE+MnxUAAVUfGNiKghg8MiyAShWDiQQbl5+ITTTBAAmNQc5ADZxXXLxYoUZSrU1IzF8wrkC4tbxFl0NNTaXNwwcAiJvX3IkgKCQgBVKGnpGUlm6BiIU4QygwWFG8RktBRbbLt0WLRYVI1yAWi1buRqj9RY8l90FPpB3Qa9YIloJ8ghMgAL0SUGIazSGyy20QeXOxTkLE6CjULGKWmUjjyl0QVykjhatyk6k0MjeUjyHy+nmGvzAtAAggBjcgANxIkK4PE22VAjXhmhKWnEtxsz10eQJuIQVylUjkouK6L0MjUBS01IGtLAcmZAAswMyANZ4BkAJQArgIZlQIex1jyYWkdjIqoqRZi8lVKeTxDKrnZFZTKvoOmU0d6tR4hrqDUbTX8rTbZhDxKluZkyFsXXCCvISco0boalV6iY8UHpHlQ8pzl1nsVo99hj4gkkAGLoABOydtJHIVGWjC56Sd2b5ojzdTkGi6WhkCilJ3UAbyd3U8KX0i0UrUzZ1cnptGTgSS9ozY6zOf5cPOamF2j2+yqNhkMryMnkSkKahJdU-F4m1cT5tVjI8k3wAQL0da9JwFe9H0cSoZCkWR1xlQoDlueUF10UpIwPcC2zGKAu27ABRAQIH7RYh3mVYHShccbynfI7C0RVNCeNDig4mUpCUYNlTyJcpD-b0dCIrx4xNM1aComjU1HaEJ1hBBdgxYVRR6Yp1Ukd8KwQW5ETsNRFwUDpqnKKRpOGWTEwZRTaKYdNYN5dS8klcQkXUPSfRRBQZRMuQzIsqzyX0OzdWPDtEjIWB9TBGDmLgzyEV8t4pXELyFDy3QZUkPKSh6LQ2kE04rOikY+AAIQwE0wXk4huwZCJjBUlj4LhHoHxyt18M0TQRXLXIylOULikXMoTLRSRqpI+r0EapJmpiYI4sCRLIE6tLc3yVpFU6fQ1WqBdVyM6QZBYI6ppOPK5v2BbRiWlaoGa3aPP2lC8kUSkpX2EVJH9S7uiRTEujVczlQUYD+hjLxFoa40mr+NNL1U1idjORFbGVaQ+N3DQtEK4o0NCmRvTeUUBuq48AGF0AoWhUDAUiUszL7bw0vYFSLE5UN5vjCrVCxLLeW4SzLWQ6b+RnmdZ9nXIxrrPP0RE-PXPZFwJEGxtFxQygUSW3VRfcQJpcDaG7ZawEgMEADkwBEXAPqYznnW5gmFWrPLP30Bxyg-U5OPUbWS3lVRdGevhyIAWXt-BNoS-VokT5PtogT7PbYrz4XuPSVDaPivxJoyXkxWdzAecpvTymPcHj9P4sStPYiTlvDSztzUq53Oql+toWFhnQ1FRUUPwKHzYdacpinnqRgJAgR0AgOBhEtxh3Jzxp8SCoyrjdB9Nz4t58cXBuAWCMJ2u3tT9pYD9Ck40pyjsKpSzqBuwSv6YlgYsAd8sYSFQr9aoKgaw1Epm+AMxtOIODKt6FQkgyrm3hi2GKfxf6oBBGCIB3UeaEj2AUP8+kvwBgIi0aow9NJTWqDIWWDIWTskAb3HeIDUKKCfMqCo5JDAHy6IiRB6tYb4XUGg0CCN7KGjkkma0-Z8HqU0hYXY-Uay7joQGMoPlHCYn2GqdE2sJGb1bKMME5E+yzEUftF4yhQqfjVIudUn5xJaKDLogk1Q0RTQXO8C2YEfhyIEGeKA1ivZ7AsF5To88bCOE8YVTos5kGSlEp0a6n5GEnigmE3OyIxaLnftoTQgiBJCg6KcYmX4ejD1sv4qRuoSLmJ7M5KxbD77cyib9TEJJIEmWqDKEuJQJSL1rog6qDl5ItKoDk10fFfJTxOOqKobwBkliGV5EZkpEHiEyRnJKSQZkgJ0HIHQlJTiRV3OSGUOt7iFGeMicSthzIN1eijVafxDmEJukqL8AMpoYn3mNfCBxBIzzdDYUoS5MnyxZmzPBbTgE83VCUUU+ZkR6UioVTED5LLzx0HsPKnjqrW1tvbJITsXbyU+bsZFLA3SUkxMiVEVzy50s4uSWGqFZD-j2A3Ju7c9mfJeLDe4dL4SCXfpTUad5K6FAcMqPieL0TVWQJ3SAQqHA4vJABEUpROgfjQj5V+FQP61HNi4IAA */
    id: "flowState",
    initial: "waitInitialPlay",
    context: ({ input }) => ({
      flowFlexibility: input.flowFlexibility,
      currentTime: input.initialFlowState.currentTime,
      currentSectionIndex: input.initialFlowState.currentSectionIndex,
      currentStepIndex: input.initialFlowState.currentStepIndex,
      flow: input.flow,
      eventQueue: [],
      flowKey: input.flow.key,
    }),
    on: {
      timeUpdate: {
        actions: "setCurrentTime",
      },
    },

    states: {
      waitInitialPlay: {
        on: {
          mediaPlaying: {
            target: "stepInitializing",
          },
        },
      },

      waitingInitialTimeUpdate: {
        on: {
          timeUpdate: {
            actions: "setCurrentTime",
            target: "stepInitializing",
          },
        },
      },

      stepInitializing: {
        entry: "initStep",
        always: {
          target: "stepActive",
        },
      },

      stepActive: {
        always: [
          {
            target: "checkStepRunTime",
          },
        ],
      },

      checkStepRunTime: {
        always: [
          {
            guard: "isStepRunTime",
            target: "stepRunning",
          },
          {
            target: "waitingForRunTime",
          },
        ],
      },

      waitingForRunTime: {
        on: {
          timeUpdate: [
            {
              actions: "setCurrentTime",
              target: "checkStepRunTime",
            },
          ],
        },
      },

      stepRunning: {
        entry: "runStep",
        always: {
          target: "stepRan",
        },
      },

      stepRan: {
        always: [
          {
            target: "checkStepEndTime",
          },
        ],
      },

      waitingForEndTime: {
        on: {
          timeUpdate: [
            {
              actions: "setCurrentTime",
              target: "checkStepEndTime",
            },
          ],
        },
      },

      checkStepEndTime: {
        always: [
          {
            guard: "isStepEndTime",
            target: "stepFinishing",
          },
          {
            target: "waitingForEndTime",
          },
        ],
      },

      stepFinishing: {
        entry: "finishStep",
        always: {
          target: "waitBlockingStep",
        },
      },

      waitBlockingStep: {
        always: [
          {
            guard: "isBlockingStep",
            actions: "waitBlockingStep",
          },
          {
            target: "stepCompleting",
          },
        ],
        on: {
          replay: {
            target: "stepInitializing",
          },
          mediaFinished: {
            description:
              "Media finished event can be dispatched when there is a blocking step at the end of the section. We should defer this event to handle it later.",
            actions: "deferMediaFinishedEvent",
          },
          forceProceedToNextStep: {
            target: "stepCompleting",
          },
        },
      },

      stepCompleting: {
        entry: "disposeStep",
        always: [
          {
            guard: "hasNextStep",
            target: "proceedingNextStep",
          },
          {
            guard: "isLastStepInSection",
            target: "waitForMediaFinish",
          },
        ],
      },

      proceedingNextStep: {
        always: {
          actions: "proceedNextStep",
          target: "stepInitializing",
        },
      },

      waitForMediaFinish: {
        description: "",
        entry: "drainDeferredMediaFinishedEvents",
        on: {
          mediaFinished: [
            {
              guard: "isLastSection",
              target: "finished",
            },
            {
              actions: "proceedNextSection",
              target: "waitingInitialTimeUpdate",
            },
          ],
        },
      },

      finished: {
        entry: "finish",
        type: "final",
      },
    },
  });

  return createActor(flowStateMachine, { input });
};

export const createFlowStateStream = (machine: ReturnType<typeof createFlowStateActor>) => fromStream(machine);
